import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiCall } from '../lib/api';
import { useCart } from '../hooks/useCart';
import { trackEvent, trackViewItem, trackAddToCart } from '../lib/analytics';
import type { Product } from '@meso/types';

export function ProductDetail() {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const cart = useCart();
  const [variantId, setVariantId] = useState<string>('');
  const [qty, setQty] = useState(1);

  const q = useQuery<{ product: Product }>({
    queryKey: ['product', handle],
    queryFn: () => apiCall<{ product: Product }>('get', `/shop/products/${handle}`),
    enabled: !!handle,
  });

  useEffect(() => {
    if (q.data?.product && !variantId) {
      const first = q.data.product.variants.find((v) => v.available) ?? q.data.product.variants[0];
      if (first) setVariantId(first.id);
    }
  }, [q.data, variantId]);

  useEffect(() => {
    if (!q.data) return;
    const p = q.data.product;
    const v = p.variants.find((v) => v.id === variantId) ?? p.variants[0]!;
    trackViewItem({
      item_id: v.id,
      item_name: p.title,
      price: parseFloat(v.price.amount),
      quantity: 1,
      item_brand: 'mesoestetic',
      item_variant: v.title,
    });
  }, [q.data, variantId]);

  if (q.isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;
  if (!q.data?.product) return <div className="p-6 text-center text-gray-500">ไม่พบสินค้า</div>;

  const product = q.data.product;
  const variant = product.variants.find((v) => v.id === variantId) ?? product.variants[0]!;
  const price = parseFloat(variant.price.amount);
  const compareAt = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;
  const onSale = compareAt && compareAt > price;

  function handleAdd() {
    cart.add({
      variantId: variant.id,
      productId: product.id,
      productHandle: product.handle,
      title: `${product.title}${variant.title !== 'Default Title' ? ` · ${variant.title}` : ''}`,
      price,
      quantity: qty,
      image: variant.image?.url ?? product.featuredImage?.url ?? null,
    });
    trackAddToCart({
      item_id: variant.id,
      item_name: product.title,
      price,
      quantity: qty,
      item_brand: 'mesoestetic',
      item_variant: variant.title,
    });
    navigate('/shop/cart');
  }

  return (
    <div className="max-w-md mx-auto pb-24">
      <Link to="/shop" className="block px-4 py-3 text-sm text-gray-500">← ย้อนกลับ</Link>

      <div className="aspect-square bg-gray-100 mb-3">
        {variant.image?.url ? (
          <img src={variant.image.url} className="w-full h-full object-cover" alt="" />
        ) : product.featuredImage?.url ? (
          <img src={product.featuredImage.url} className="w-full h-full object-cover" alt="" />
        ) : null}
      </div>

      <div className="px-4 space-y-3">
        <h1 className="text-lg font-bold">{product.title}</h1>

        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-bold">฿{price.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
          {onSale && (
            <div className="text-sm text-gray-400 line-through">
              ฿{compareAt!.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </div>
          )}
        </div>

        {product.options[0] && product.variants.length > 1 && (
          <div>
            <div className="form-label">ตัวเลือก</div>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVariantId(v.id)}
                  disabled={!v.available}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${
                    v.id === variantId ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                  } ${!v.available ? 'opacity-40 line-through' : ''}`}
                >
                  {v.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="form-label">จำนวน</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-9 h-9 border border-gray-300 rounded-lg">−</button>
            <span className="w-10 text-center font-semibold">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)}
                    className="w-9 h-9 border border-gray-300 rounded-lg">+</button>
          </div>
        </div>

        {product.description && (
          <div className="text-sm text-gray-600 whitespace-pre-line pt-2 border-t border-gray-100">
            {product.description}
          </div>
        )}
      </div>

      <div className="fixed bottom-16 left-0 right-0 p-3 bg-white border-t border-gray-200 z-40">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleAdd}
            disabled={!variant.available}
            className={`btn btn-block ${variant.available ? 'btn-primary' : 'bg-gray-200 text-gray-500'}`}
          >
            {variant.available ? `เพิ่มในตะกร้า · ฿${(price * qty).toLocaleString('th-TH')}` : 'หมดสต็อก'}
          </button>
        </div>
      </div>
    </div>
  );
}
